import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Flex,
  Spinner,
  Button,
  useColorModeValue,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Badge,
  Progress,
  CircularProgress,
  CircularProgressLabel,
  Tooltip,
} from '@chakra-ui/react';
import { 
  FaBullseye, 
  FaArrowRight,
  FaChartLine,
  FaStar,
  FaSmile,
  FaTrophy,
  FaArrowUp,
  FaArrowDown,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getYearData } from '../services/yearService';
import { getReviews } from '../services/reviewService';
import { logger } from '../utils/logger';

// Discrete slider positions - defined outside component to prevent re-renders
const SLIDER_POSITIONS = [1, 3, 5, 10, 15, 20];

/**
 * SimpleGoalsWidget - Simple goals widget with adjustable target rank slider
 * Shows key metrics based on selected rank position with CTA
 */
const SimpleGoalsWidget = () => {
  const navigate = useNavigate();
  const [yearData, setYearData] = useState([]);
  const [reviewsData, setReviewsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [targetPosition, setTargetPosition] = useState(5);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const sliderTrackBg = useColorModeValue('gray.200', 'gray.600');
  const hoverBorderColor = useColorModeValue('blue.200', 'blue.600');
  const accentGradient = useColorModeValue(
    'linear(to-r, blue.500, blue.600)',
    'linear(to-r, blue.400, blue.500)'
  );
  const hoverGradient = useColorModeValue(
    'linear(to-r, blue.600, blue.700)',
    'linear(to-r, blue.500, blue.600)'
  );
  const metricBg = useColorModeValue('gray.50', 'gray.700');
  const progressSectionBg = useColorModeValue('blue.50', 'gray.700');

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const [yearResult, reviewsResult] = await Promise.all([
          getYearData(),
          getReviews(),
        ]);
        
        if (mounted) {
          setYearData(yearResult || []);
          setReviewsData(reviewsResult || []);
          setIsLoading(false);
        }
      } catch (error) {
        logger.error('Error fetching data for Simple Goals Widget:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  // Helper to calculate avg reviews per month
  const calculateAvgMonthlyReviews = useCallback((business) => {
    const counts = Object.values(business?.monthlyReviewCounts || business?.historicalData?.monthlyReviewCounts || business?.data?.monthlyReviewCounts || {});
    if (!counts.length) return 0;
    return counts.reduce((a, b) => a + b, 0) / counts.length;
  }, []);

  // Helper to calculate sentiment score
  const calculateSentimentScore = useCallback((breakdown) => {
    if (!breakdown) return 0;
    const { positive = 0, neutral = 0, negative = 0 } = breakdown;
    const total = positive + neutral + negative;
    return total > 0 ? ((positive + neutral * 0.5) / total) * 100 : 0;
  }, []);

  // Combine reviews with year data
  const combinedData = useMemo(() => {
    if (!reviewsData.length) return [];
    const reviews = Array.isArray(reviewsData) ? reviewsData : reviewsData.reviews || [];
    return reviews.map(business => {
      const yearInfo = yearData.find(y => y.maps_uri === business.maps_uri);
      return {
        ...business,
        historicalData: yearInfo?.data || null,
        monthlyReviewCounts: yearInfo?.data?.monthlyReviewCounts || {},
        sentimentBreakdown: yearInfo?.data?.sentimentBreakdown || null,
      };
    });
  }, [reviewsData, yearData]);

  // Calculate independent rankings for each metric
  // Total reviews ranking (sorted by rating_count)
  const totalReviewsRankings = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .filter(b => b.rating_count > 0)
      .sort((a, b) => b.rating_count - a.rating_count);
  }, [combinedData]);

  // Monthly reviews ranking (sorted by avg monthly reviews) - INDEPENDENT
  const monthlyReviewsRankings = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map(b => ({
        ...b,
        avgMonthlyReviews: calculateAvgMonthlyReviews(b),
      }))
      .filter(b => b.avgMonthlyReviews > 0)
      .sort((a, b) => b.avgMonthlyReviews - a.avgMonthlyReviews);
  }, [combinedData, calculateAvgMonthlyReviews]);

  // Sentiment ranking (sorted by sentiment score) - INDEPENDENT
  const sentimentRankings = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map(b => ({
        ...b,
        sentimentScore: calculateSentimentScore(b.sentimentBreakdown),
      }))
      .filter(b => b.sentimentScore > 0)
      .sort((a, b) => b.sentimentScore - a.sentimentScore);
  }, [combinedData, calculateSentimentScore]);

  // Calculate target thresholds based on slider position - using INDEPENDENT rankings
  const targetThresholds = useMemo(() => {
    if (!totalReviewsRankings.length) return null;

    // Get the Nth position value from each independent ranking
    // Position 1 = highest, Position 5 = 5th highest, etc.
    
    // Monthly reviews: get the Nth highest monthly review value
    const effectiveMonthlyTarget = Math.min(targetPosition, monthlyReviewsRankings.length);
    const monthlyTarget = effectiveMonthlyTarget > 0
      ? Math.round(monthlyReviewsRankings[effectiveMonthlyTarget - 1]?.avgMonthlyReviews * 10) / 10
      : 0;

    // Total reviews: get the Nth highest total review count
    const effectiveTotalTarget = Math.min(targetPosition, totalReviewsRankings.length);
    const totalReviewsTarget = effectiveTotalTarget > 0
      ? totalReviewsRankings[effectiveTotalTarget - 1]?.rating_count || 0
      : 0;

    // Sentiment: get the Nth highest sentiment score
    const effectiveSentimentTarget = Math.min(targetPosition, sentimentRankings.length);
    const sentimentTarget = effectiveSentimentTarget > 0
      ? Math.round(sentimentRankings[effectiveSentimentTarget - 1]?.sentimentScore)
      : 85;

    // Get the top business names for context
    const topReviewsBusiness = totalReviewsRankings[0]?.name || 'Market Leader';
    const topMonthlyBusiness = monthlyReviewsRankings[0]?.name || 'Market Leader';
    const topSentimentBusiness = sentimentRankings[0]?.name || 'Market Leader';

    return {
      monthlyReviews: monthlyTarget,
      totalReviews: totalReviewsTarget,
      sentimentScore: sentimentTarget,
      topReviewsBusiness,
      topMonthlyBusiness,
      topSentimentBusiness,
      marketLeader: totalReviewsRankings[0]?.rating_count || 0,
      monthlyLeader: Math.round(monthlyReviewsRankings[0]?.avgMonthlyReviews * 10) / 10 || 0,
      sentimentLeader: Math.round(sentimentRankings[0]?.sentimentScore) || 0,
    };
  }, [totalReviewsRankings, monthlyReviewsRankings, sentimentRankings, targetPosition]);

  // Calculate market averages
  const marketStats = useMemo(() => {
    if (!combinedData.length) return null;
    
    const totalReviews = combinedData.reduce((sum, b) => sum + (b.rating_count || 0), 0);
    const avgReviews = Math.round(totalReviews / combinedData.length);
    
    const monthlyReviews = monthlyReviewsRankings.map(b => b.avgMonthlyReviews);
    const avgMonthly = monthlyReviews.length ? (monthlyReviews.reduce((a, b) => a + b, 0) / monthlyReviews.length).toFixed(1) : 0;
    
    const sentiments = sentimentRankings.map(b => b.sentimentScore);
    const avgSentiment = sentiments.length ? Math.round(sentiments.reduce((a, b) => a + b, 0) / sentiments.length) : 0;
    
    return {
      avgReviews,
      avgMonthly: parseFloat(avgMonthly),
      avgSentiment,
      totalBusinesses: combinedData.length,
    };
  }, [combinedData, monthlyReviewsRankings, sentimentRankings]);

  // Calculate overall goal progress
  const overallProgress = useMemo(() => {
    if (!marketStats || !targetThresholds) return 0;
    
    const reviewProgress = Math.min((marketStats.avgReviews / targetThresholds.totalReviews) * 100, 100) || 0;
    const monthlyProgress = Math.min((marketStats.avgMonthly / targetThresholds.monthlyReviews) * 100, 100) || 0;
    const sentimentProgress = Math.min((marketStats.avgSentiment / targetThresholds.sentimentScore) * 100, 100) || 0;
    
    return Math.round((reviewProgress + monthlyProgress + sentimentProgress) / 3);
  }, [marketStats, targetThresholds]);

  // Get current index from position
  const currentIndex = SLIDER_POSITIONS.indexOf(targetPosition);
  const sliderIndex = currentIndex >= 0 ? currentIndex : 0;

  // Handle slider change - index-based for clean snapping
  const handleSliderChange = useCallback((index) => {
    setTargetPosition(SLIDER_POSITIONS[index]);
  }, []);

  const handleViewGoals = useCallback(() => {
    navigate('/playbook/goals');
  }, [navigate]);

  if (isLoading) {
    return (
      <Box
        bg={cardBg}
        borderRadius="2xl"
        p={8}
        h="100%"
        minH="480px"
        boxShadow="sm"
        border="1px solid"
        borderColor={borderColor}
      >
        <Flex justify="center" align="center" h="100%">
          <Spinner size="lg" color="blue.500" thickness="3px" />
        </Flex>
      </Box>
    );
  }

  return (
    <Box
      bg={cardBg}
      borderRadius="2xl"
      overflow="hidden"
      h="100%"
      minH="480px"
      boxShadow="sm"
      border="1px solid"
      borderColor={borderColor}
      transition="all 0.3s ease"
      _hover={{
        boxShadow: 'md',
        borderColor: hoverBorderColor,
      }}
    >
      <VStack spacing={0} h="100%">
        {/* Header */}
        <HStack 
          w="100%" 
          px={6} 
          pt={5} 
          pb={4}
          justify="space-between"
          align="center"
        >
          <HStack spacing={3}>
            <Flex
              w="36px"
              h="36px"
              borderRadius="lg"
              bgGradient={accentGradient}
              justify="center"
              align="center"
            >
              <Icon as={FaBullseye} color="white" boxSize={4} />
            </Flex>
            <Text fontWeight="600" fontSize="1.3em" color={textColor} textTransform="uppercase" fontStyle="italic">
              Goals
            </Text>
          </HStack>
          <Badge
            colorScheme="blue"
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
            fontWeight="bold"
          >
            Top {targetPosition}
          </Badge>
        </HStack>

        {/* Slider Section */}
        <Box w="100%" px={6} pb={4}>
          <Slider
            min={0}
            max={SLIDER_POSITIONS.length - 1}
            step={1}
            value={sliderIndex}
            onChange={handleSliderChange}
          >
            <SliderTrack 
              bg={sliderTrackBg} 
              h="8px" 
              borderRadius="full"
            >
              <SliderFilledTrack 
                bgGradient="linear(to-r, blue.400, blue.500)"
                borderRadius="full"
              />
            </SliderTrack>
            <SliderThumb
              boxSize={5}
              bg="white"
              border="2px solid"
              borderColor="blue.500"
              boxShadow="md"
              _focus={{ boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.4)' }}
            />
          </Slider>
          
          {/* Slider Labels */}
          <HStack justify="space-between" mt={2}>
            {SLIDER_POSITIONS.map((pos) => (
              <Text
                key={pos}
                fontSize="xs"
                fontWeight={targetPosition === pos ? 'bold' : 'medium'}
                color={targetPosition === pos ? 'blue.500' : subtitleColor}
                cursor="pointer"
                onClick={() => setTargetPosition(pos)}
                _hover={{ color: 'blue.500' }}
                transition="color 0.15s"
              >
                #{pos}
              </Text>
            ))}
          </HStack>
        </Box>

        {/* Metrics Display */}
        <Box flex="1" w="100%" px={6} py={2}>
          <VStack spacing={3}>
            {/* Total Reviews */}
            <Tooltip label={`Market leader: ${targetThresholds?.topReviewsBusiness}`} placement="top" hasArrow>
              <Box w="100%" bg={metricBg} p={3} borderRadius="xl" cursor="default">
                <HStack justify="space-between" mb={2}>
                  <HStack spacing={3}>
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="lg"
                      bg="orange.100"
                      justify="center"
                      align="center"
                    >
                      <Icon as={FaStar} color="orange.500" boxSize={4} />
                    </Flex>
                    <Text fontSize="sm" color={subtitleColor} fontWeight="medium">
                      Total Reviews
                    </Text>
                  </HStack>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    {targetThresholds?.totalReviews?.toLocaleString() || '—'}
                  </Text>
                </HStack>
                <HStack justify="space-between" fontSize="2xs" color={subtitleColor} mb={1}>
                  <Text>Mkt avg: {marketStats?.avgReviews?.toLocaleString() || 0}</Text>
                  <Text>Leader: {targetThresholds?.marketLeader?.toLocaleString() || 0}</Text>
                </HStack>
                <Progress 
                  value={targetThresholds?.totalReviews ? (marketStats?.avgReviews / targetThresholds?.totalReviews) * 100 : 0}
                  size="xs" 
                  colorScheme="orange"
                  borderRadius="full"
                  bg={sliderTrackBg}
                />
              </Box>
            </Tooltip>

            {/* Monthly Growth */}
            <Tooltip label={`Fastest growing: ${targetThresholds?.topMonthlyBusiness}`} placement="top" hasArrow>
              <Box w="100%" bg={metricBg} p={3} borderRadius="xl" cursor="default">
                <HStack justify="space-between" mb={2}>
                  <HStack spacing={3}>
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="lg"
                      bg="green.100"
                      justify="center"
                      align="center"
                    >
                      <Icon as={FaChartLine} color="green.500" boxSize={4} />
                    </Flex>
                    <Text fontSize="sm" color={subtitleColor} fontWeight="medium">
                      Monthly Avg
                    </Text>
                  </HStack>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    {targetThresholds?.monthlyReviews || '—'}
                    <Text as="span" fontSize="xs" fontWeight="normal" color={subtitleColor} ml={1}>
                      /mo
                    </Text>
                  </Text>
                </HStack>
                <HStack justify="space-between" fontSize="2xs" color={subtitleColor} mb={1}>
                  <Text>Mkt avg: {marketStats?.avgMonthly || 0}/mo</Text>
                  <Text>Leader: {targetThresholds?.monthlyLeader || 0}/mo</Text>
                </HStack>
                <Progress 
                  value={targetThresholds?.monthlyReviews ? (marketStats?.avgMonthly / targetThresholds?.monthlyReviews) * 100 : 0}
                  size="xs" 
                  colorScheme="green"
                  borderRadius="full"
                  bg={sliderTrackBg}
                />
              </Box>
            </Tooltip>

            {/* Sentiment Score */}
            <Tooltip label={`Best rated: ${targetThresholds?.topSentimentBusiness}`} placement="top" hasArrow>
              <Box w="100%" bg={metricBg} p={3} borderRadius="xl" cursor="default">
                <HStack justify="space-between" mb={2}>
                  <HStack spacing={3}>
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="lg"
                      bg="blue.100"
                      justify="center"
                      align="center"
                    >
                      <Icon as={FaSmile} color="blue.500" boxSize={4} />
                    </Flex>
                    <Text fontSize="sm" color={subtitleColor} fontWeight="medium">
                      Sentiment
                    </Text>
                  </HStack>
                  <Text fontSize="lg" fontWeight="bold" color={textColor}>
                    {targetThresholds?.sentimentScore || '—'}%
                  </Text>
                </HStack>
                <HStack justify="space-between" fontSize="2xs" color={subtitleColor} mb={1}>
                  <Text>Mkt avg: {marketStats?.avgSentiment || 0}%</Text>
                  <Text>Leader: {targetThresholds?.sentimentLeader || 0}%</Text>
                </HStack>
                <Progress 
                  value={targetThresholds?.sentimentScore ? (marketStats?.avgSentiment / targetThresholds?.sentimentScore) * 100 : 0}
                  size="xs" 
                  colorScheme="blue"
                  borderRadius="full"
                  bg={sliderTrackBg}
                />
              </Box>
            </Tooltip>
          </VStack>

          {/* Goal Progress Summary - New Section */}
          <Box mt={4} bg={progressSectionBg} p={4} borderRadius="xl">
            <HStack spacing={4} align="center">
              {/* Circular Progress */}
              <CircularProgress 
                value={overallProgress} 
                size="60px"
                thickness="8px"
                color={overallProgress >= 80 ? 'green.400' : overallProgress >= 50 ? 'yellow.400' : 'orange.400'}
                trackColor={sliderTrackBg}
              >
                <CircularProgressLabel>
                  <Text fontSize="sm" fontWeight="bold" color={textColor}>
                    {overallProgress}%
                  </Text>
                </CircularProgressLabel>
              </CircularProgress>

              {/* Progress Text */}
              <VStack align="start" spacing={1} flex="1">
                <HStack spacing={2}>
                  <Icon 
                    as={FaTrophy} 
                    color={overallProgress >= 80 ? 'green.500' : overallProgress >= 50 ? 'yellow.500' : 'orange.500'} 
                    boxSize={3.5} 
                  />
                  <Text fontSize="sm" fontWeight="600" color={textColor}>
                    Goal Progress
                  </Text>
                </HStack>
                <HStack spacing={1} fontSize="xs" color={subtitleColor}>
                  <Icon 
                    as={overallProgress >= 50 ? FaArrowUp : FaArrowDown} 
                    color={overallProgress >= 50 ? 'green.500' : 'red.500'} 
                    boxSize={2.5}
                  />
                  <Text>
                    {overallProgress >= 80 ? 'Excellent progress!' : 
                     overallProgress >= 50 ? 'On track to goals' : 
                     'Room for improvement'}
                  </Text>
                </HStack>
                <Text fontSize="2xs" color={subtitleColor}>
                  {marketStats?.totalBusinesses || 0} businesses tracked in market
                </Text>
              </VStack>
            </HStack>
          </Box>
        </Box>

        {/* CTA Button */}
        <Box w="100%" px={6} pb={6} pt={2}>
          <Button
            w="100%"
            size="lg"
            bgGradient={accentGradient}
            color="white"
            fontWeight="600"
            borderRadius="xl"
            rightIcon={<FaArrowRight />}
            onClick={handleViewGoals}
            _hover={{
              bgGradient: hoverGradient,
              transform: 'translateY(-2px)',
              boxShadow: 'lg',
            }}
            _active={{
              transform: 'translateY(0)',
            }}
            transition="all 0.2s ease"
          >
            Set Your Goals
          </Button>
        </Box>
      </VStack>
    </Box>
  );
};

export default SimpleGoalsWidget;
